import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { MapHelperService } from 'src/services/map-helper.service';
import { GeocoderService } from '../../services/geocoder.service';

declare var google: any;

@Component({
  selector: 'geocoder',
  templateUrl: './geocoder.component.html',
  styleUrls: ['./geocoder.component.css'],
  providers: [GeocoderService]
})
export class GeocoderComponent {

  @Input() layerSuffix: string = 'default';
  @Input() layerDisplaySuffix: string = '';
  @Input() searchAddress: string = '';
  @Input() isSearchDisabled: boolean = false;
  @Input() resetComponent: boolean = false;

  @Output() searchAddressChange = new EventEmitter<string>();
  @Output() pointSelected = new EventEmitter<{ lng: number, lat: number }>();
  @Output() isGeocoderReady = new EventEmitter<boolean>();
  @Output() dropdownVisibilityChange = new EventEmitter<boolean>();

  // Para el punto de referencia
  isMapSelectionActive: boolean = false;
  selectedPoint: { lat: number, lng: number } = null;
  mapClickHandler: any = null;
  directionsSuggestions: any[] = [];
  showSuggestions: boolean = false;
  isSearching: boolean = false;
  inputFocused: boolean = false;
  selectedPointMarker: any = null;
  searchSubject = new Subject<string>();
  
  isPlacesLibraryLoaded: boolean = false;
  placesInitTries = 0;

  constructor(private geocoderService: GeocoderService, private http: HttpClient, private mapHelperService: MapHelperService) {}

  async ngOnInit(): Promise<void> {
    await this.mapHelperService.waitForMapToRender();
    this.geocoderService.createLayer(this.layerSuffix, this.layerDisplaySuffix);
    
    this.initGooglePlacesServices();
    
    // para no hacer un millon de peticiones, se espera que deje de escribir un segundo
    this.searchSubject.pipe( debounceTime(500), distinctUntilChanged()).subscribe(searchTerm => {
      console.log("debounce", searchTerm);
      
      this.searchAddress = searchTerm;
      this.searchAddressChange.emit(this.searchAddress);
      this.searchLocationConPlaces(false);
    });
  }

  private initGooglePlacesServices(): void {
    // intenta cargar el servicio 5 veces
    this.placesInitTries++;
    if(this.placesInitTries >= 5) 
      return;
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      this.isPlacesLibraryLoaded = true;
      console.log('google cargó');
      this.isGeocoderReady.emit(true);
      
    } else {
      console.log('google no cargó');
      setTimeout(() => this.initGooglePlacesServices(), 500);
    }
  }

  ngOnDestroy(): void {
    if (this.mapClickHandler) {
      this.mapHelperService.RemoveEventFromMap('click', this.mapClickHandler);
    }
    this.removePointMarker();
    this.searchSubject.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes['resetComponent'] && changes['resetComponent'].currentValue == true){
      this.clearSearch();
    }
  }

  onInputFocus(): void {
    this.inputFocused = true;
    this.updateDropdownVisibility();
  }

  onInputNoFocus(): void {
    setTimeout(() => {
      this.inputFocused = false;
      this.updateDropdownVisibility();
    }, 200);
  }

  private updateDropdownVisibility(): void {
    const isVisible = this.showSuggestions && this.inputFocused && this.directionsSuggestions.length > 0;
    this.dropdownVisibilityChange.emit(isVisible);
  }
  
  async searchLocation(): Promise<void> {
    if (!this.searchAddress || this.searchAddress.trim().length < 3) {
      this.directionsSuggestions = [];
      this.showSuggestions = false;
      this.geocoderService.clearLayer();
      return;
    }

    this.isSearching = true;
    try {
      //                   +-decimales  espacio  +-decimales
      const coordRegex = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;
      const isCoordinates = coordRegex.test(this.searchAddress);
      var petition = ""
      if(isCoordinates){
        petition = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${this.searchAddress}&key=${environment.googleMapsApiKey}&components=country:MX`;
      } else {
        petition = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(this.searchAddress)}&components=country:MX&key=${environment.googleMapsApiKey}`;
      }
      const response = await firstValueFrom(
        this.http.get(
          petition
        )
      );

      this.directionsSuggestions = (response as any).results || [];
      this.showSuggestions = this.directionsSuggestions.length > 0;
      this.updateDropdownVisibility();
    } catch (error) {
      console.error('Error al buscar direcciones:', error);
      this.directionsSuggestions = [];
      this.showSuggestions = false;
      this.updateDropdownVisibility();
    } finally {
      this.isSearching = false;
    }
  }

  async searchLocationConPlaces(startSearch: boolean): Promise<void> {
    if (!this.searchAddress || this.searchAddress.trim().length <= 3) {
      this.directionsSuggestions = [];
      this.showSuggestions = false;
      this.geocoderService.clearLayer();
      return;
    }

    this.isSearching = true;
    try {
      //                   +-decimales  espacio  +-decimales
      const coordRegex = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/;
      const isCoordinates = coordRegex.test(this.searchAddress);
      
      if(isCoordinates){
        if (!this.isPlacesLibraryLoaded) {
          console.error('google no disponible');
          return;
        }

        const [lat, lng] = this.searchAddress.split(',').map(coord => parseFloat(coord.trim()));
        const nearbyPlaces = await this.getPlaceDetailsByCoords(lat, lng);
        this.directionsSuggestions = nearbyPlaces;

        if (startSearch) {
          this.setSelectedPoint(lng, lat);
          this.mapHelperService.FlyToCamera(lat.toString(),lng.toString(),'15',0);
        }
      } else {
        if (!this.isPlacesLibraryLoaded) {
          console.error('google no disponible');
          return;
        }

        const suggestions = await this.getAutocompleteSuggestions(this.searchAddress);
        
        if (suggestions && suggestions.length > 0) {
          // busca sólo 5 para no saturar
          const detailsPromises = suggestions.slice(0, 5).map((suggestion: any) => 
            this.getPlaceDetailsByPlaceId(suggestion.placePrediction.placeId)
          );
          
          const details = await Promise.all(detailsPromises);
          this.directionsSuggestions = details.filter(d => d !== null);

          if (startSearch && this.directionsSuggestions.length > 0) {
            this.selectSuggestion(this.directionsSuggestions[0]);
          }
        } else {
          this.directionsSuggestions = [];
        }
      }
      
      this.showSuggestions = this.directionsSuggestions.length > 0;
      this.updateDropdownVisibility();
    } catch (error) {
      console.error('Error al buscar direcciones:', error);
      this.directionsSuggestions = [];
      this.showSuggestions = false;
      this.updateDropdownVisibility();
    } finally {
      this.isSearching = false;
    }
  }

  async getAutocompleteSuggestions(input: string): Promise<any[]> {
    try {
      const { AutocompleteSuggestion } = await google.maps.importLibrary('places');
      
      const request = {
        input: input,
        includedRegionCodes: ['mx'],
      };

      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      return suggestions || [];
    } catch (error) {
      console.error('Error en AutocompleteSuggestion:', error);
      return [];
    }
  }
  
  async getPlaceDetailsByPlaceId(placeId: string): Promise<any> {
    try {
      const { Place } = await google.maps.importLibrary('places');
      
      const place = new Place({ id: placeId });
      
      await place.fetchFields({
        fields: ['formattedAddress', 'location', 'addressComponents', 'displayName']
      });
      
      const placeDetails  = {
        formatted_address: place.formattedAddress,
        name: place.displayName,
        geometry: {
          location: place.location
        },
        address_components: place.addressComponents
      };
      return placeDetails;
    } catch (error) {
      return null;
    }
  }

  private async getPlaceDetailsByCoords(lat: number, lng: number): Promise<any[]> {
    try {
      const { Place } = await google.maps.importLibrary('places');
      
      const request = {
        locationRestriction: {
          center: { lat, lng },
          radius: 500
        },
        maxResultCount: 5,
        fields: ['formattedAddress', 'location', 'displayName', 'addressComponents']
      };

      const { places } = await Place.searchNearby(request);
      
      if (places && places.length > 0) {
        const placesDetails = places.map((place: any) => ({
          formatted_address: place.formattedAddress,
          name: place.displayName,
          geometry: {
            location: place.location
          },
          address_components: place.addressComponents
        }));
        return placesDetails;
      }
      
      return [];
    } catch (error) {
      return [];
    }
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchAddress);
  }

  selectSuggestion(suggestion: any): void {
    this.searchAddress = suggestion.formatted_address;
    this.showSuggestions = false;
    this.directionsSuggestions = [];

    const location = suggestion.geometry.location;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    this.setSelectedPoint(lng, lat);

    this.mapHelperService.FlyToCamera(lat.toString(),lng.toString(),'15',0);
  }


  public clearSearch(): void {
    this.searchAddress = '';
    this.searchAddressChange.emit(this.searchAddress);
    this.selectedPoint = null;
    this.directionsSuggestions = [];
    this.showSuggestions = false;
    this.removePointMarker();
    // if (this.isMapSelectionActive) {
    //   this.toggleMapSelection();
    // }
  }


  // toggleMapSelection(): void {
  //   this.isMapSelectionActive = !this.isMapSelectionActive;

  //   if (this.isMapSelectionActive) {
  //     this.mapClickHandler = (e: any) => {
  //       const { lng, lat } = e.lngLat;
  //       this.setSelectedPoint(lat, lng);
  //       this.isMapSelectionActive = false;
  //       if (this.mapClickHandler) {
  //         this.mapHelperService.RemoveEventFromMap('click', this.mapClickHandler);
  //         this.mapClickHandler = null;
  //       }
  //     };
  //     this.mapHelperService.addEventOnMapClick(this.mapClickHandler);
  //     this.mapHelperService._map.getCanvas().style.cursor = 'crosshair';
  //   } else {
  //     if (this.mapClickHandler) {
  //       this.mapHelperService.RemoveEventFromMap('click', this.mapClickHandler);
  //       this.mapClickHandler = null;
  //     }
  //     this.mapHelperService._map.getCanvas().style.cursor = '';
  //   }
  // }

  setSelectedPoint(lng: number, lat: number): void {
    this.selectedPoint = { lng, lat };
    console.log('punto seleccionado', this.selectedPoint);
    
    this.addPointMarker(lat, lng);
    this.pointSelected.emit(this.selectedPoint);
  }

  addPointMarker(lat: number, lng: number): void {
    const geoJson = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          },
          properties: {}
        }
      ]
    };
    this.geocoderService.clearLayer();
    this.mapHelperService.UpdateLayerGeoJson(this.geocoderService.sourceName, geoJson);
    this.mapHelperService.MoveLayerToTop(this.geocoderService.layerName);
    // this.removePointMarker();
    // const elemento = document.createElement('div');
    // elemento.className = 'direction-point-marker';
    // elemento.style.backgroundImage = 'url(assets/img/map.png)';
    // elemento.style.width = '30px';
    // elemento.style.height = '40px';
    // elemento.style.backgroundSize = '100%';
    // elemento.style.backgroundRepeat = 'no-repeat';
    // elemento.style.backgroundPosition = 'center';
    // elemento.style.cursor = 'pointer';
      
    // if (typeof mapboxgl !== 'undefined') {
    //   this.selectedPointMarker = new mapboxgl.Marker(elemento).setLngLat([lng, lat]).addTo(this.mapHelperService._map);
    // }
  }
  
  removePointMarker(): void {
    // if (this.selectedPointMarker) {
    //   this.selectedPointMarker.remove();
    //   this.selectedPointMarker = null;
    // }
    this.geocoderService.clearLayer();
  }
}
